import { useCallback, useEffect, useMemo } from "react";
import { Button } from "react-bootstrap";
import { useHistory } from "react-router";
import AppointmentCalendarICS from "src/components/shared/appointments/AppointmentCalendarICS";
import { BROWSER_TIME_ZONE, DATE_TIME_FORMATS, ROUTES } from "src/constants";
import { usePatientSelfSchedulingContext } from "src/hooks/patient_scheduling/usePatientSelfSchedulingContext";
import { useLobbiePatient } from "src/hooks/patients/useLobbiePatient";
import { useLogoutUser } from "src/hooks/user";
import {
    dayjsFormatUnix,
    daysjsFromUnix,
    isEmailAutoGenerated,
    logDev,
    notify,
    pluralize,
} from "src/utils";
import { sortById } from "src/utils/sorter";

const PatientSchedulingBookingConfirmed: React.FC = () => {
    const { replace } = useHistory();
    const { selfSchedule, selfScheduleSettings } = usePatientSelfSchedulingContext();

    const [user, , getCurrentPatient] = useLobbiePatient(selfScheduleSettings?.accountPublicUuid);
    useEffect(() => {
        if (!user) {
            getCurrentPatient().catch(console.error);
        }
    }, [user, getCurrentPatient]);

    const [logout] = useLogoutUser();
    const appointment = useMemo(
        () => selfSchedule?.appointments && sortById(selfSchedule.appointments).first(),
        [selfSchedule?.appointments],
    );

    const appointmentId = useMemo(() => appointment?.id, [appointment]);
    const userActivated = useMemo(() => user?.activated, [user]);

    const patientName = useMemo(
        () =>
            selfSchedule?.accountPatient?.firstName !==
            selfSchedule?.selectedAccountPatient?.firstName
                ? selfSchedule?.selectedAccountPatient?.name
                : "you",
        [selfSchedule?.accountPatient, selfSchedule?.selectedAccountPatient],
    );

    const handleViewAppointments = useCallback(() => {
        if (userActivated) {
            replace(ROUTES.patientHome);
        } else if (typeof userActivated === "boolean" && !userActivated) {
            logDev(
                "PatientSchedulingBookingConfirmedV2.handleViewAppointments - User NOT activated. Return to login.",
            );
            notify({
                level: "info",
                title: "Please activate your account.",
                message:
                    "Before viewing your appointments, please activate your account via email.",
            });
            logout().catch(console.error);
        }
    }, [userActivated, replace, logout]);

    const handleFillOutForms = useCallback(() => {
        if (userActivated && appointmentId) {
            replace(ROUTES.patientHome);
        } else if (typeof userActivated === "boolean" && !userActivated) {
            logDev(
                "PatientSchedulingBookingConfirmedV2.handleFillOutForms - User NOT activated. Return to login.",
            );
            notify({
                level: "info",
                title: "Please activate your account.",
                message: "Before viewing your forms, please activate your account via email.",
            });
            logout().catch(console.error);
        }
    }, [userActivated, appointmentId, replace, logout]);

    const toDateString = useCallback((n: number | null) => {
        if (!n) return "";
        const day = daysjsFromUnix(n, BROWSER_TIME_ZONE);
        return day.format(DATE_TIME_FORMATS.Appointment.DateFormat2NoYear);
    }, []);

    const toTimeString = useCallback((n: number | null) => {
        if (!n) return "";
        return dayjsFormatUnix(n, BROWSER_TIME_ZONE, DATE_TIME_FORMATS.Appointment.PatientHomeTime);
    }, []);

    const practitionerName = useMemo(() => {
        if (!selfSchedule || !selfScheduleSettings) return "";
        for (const practitioner of selfScheduleSettings.practitioners) {
            if (practitioner.practitionerId === selfSchedule.practitionerId) {
                return practitioner.practitionerName;
            }
        }
    }, [selfSchedule, selfScheduleSettings]);

    if (!location || !user || !selfSchedule || !selfScheduleSettings) return null;

    return (
        <div className="col">
            <div className="row mb-2">
                <div className="col">
                    <h3 className="extra-bold mt-4">Booking confirmed</h3>
                </div>
            </div>{" "}
            <div className="row">
                <div className="col text-left">
                    <div className="body-normal semi-bold gray2">
                        <span className="text-primary bold">{practitionerName}</span> will see{" "}
                        {patientName} on{" "}
                        <span className="text-primary bold">{toDateString(selfSchedule.date)}</span>{" "}
                        at{" "}
                        <span className="text-primary bold">{toTimeString(selfSchedule.time)}</span>
                        {appointment?.telehealth ? " via telehealth." : " at "}
                        <span className="text-primary bold">
                            {(() => {
                                if (appointment?.telehealth) {
                                    return "";
                                } else {
                                    return selfSchedule?.location?.name;
                                }
                            })()}
                        </span>
                    </div>
                    {user.email && !isEmailAutoGenerated(user.email) && (
                        <div className="body-normal my-3 semi-bold gray2">
                            A confirmation email with{" "}
                            {selfScheduleSettings.appointmentPatientFieldName?.toLowerCase()}{" "}
                            details has been sent to&nbsp;
                            <span className="text-primary bold">{user.email}</span>
                        </div>
                    )}
                </div>
            </div>
            {appointment && (
                <div className="row my-3">
                    <div className="col text-left">
                        <AppointmentCalendarICS
                            appointmentId={appointment?.id}
                            message={{
                                text: "Add to calendar",
                                className: "ml-2 text-primary body-normal semi-bold pointer",
                            }}
                            icon={{ size: "2em" }}
                            isInRow={true}
                            buttonClassName="badge border"
                        />
                    </div>
                </div>
            )}
            <div className="row border-top py-3">
                <div className="col">
                    {appointment?.formCount && appointment?.formCount > 0 ? (
                        <>
                            <h5 className="extra-bold mt-3">
                                You have {appointment?.formCount} {pluralize("form")} to complete
                            </h5>
                            <span>
                                Click on the buttons below to complete the forms for your upcoming
                                &nbsp;
                                {selfScheduleSettings.appointmentPatientFieldName?.toLowerCase()} or
                                view all your{" "}
                                {selfScheduleSettings.appointmentPatientFieldName?.toLowerCase()}s.
                            </span>
                            <Button
                                className="w-100 my-4"
                                variant="primary"
                                onClick={handleFillOutForms}
                            >
                                Fill out forms
                            </Button>
                        </>
                    ) : null}
                    <Button
                        className="w-100"
                        variant="outline-primary"
                        onClick={handleViewAppointments}
                    >
                        {user.activated
                            ? `View all my ${selfScheduleSettings.appointmentPatientFieldName?.toLowerCase()}s`
                            : "Go to login"}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default PatientSchedulingBookingConfirmed;
